/**
 * A single Bruno request file (`.bru`, or `.yml` since Bruno 3.1) → one
 * collection with one request. Thin wrappers over the shared Bruno pipeline
 * (`bruno/`): the same reader and the same mapping a whole collection folder
 * goes through, so a request imports identically alone or inside its folder.
 */
import { bruRequestToNode } from './bruno/fromBru';
import { ymlItemToNode } from './bruno/fromYaml';
import { emptyDefaults } from './bruno/ir';
import { irRequestToNormalized, irToNormalized } from './bruno/toNormalized';
import { defaultRequestAuth } from '../requestAuth';

/**
 * Parse a single .bru file text into a NormalizedRequest.
 * Scripts are kept and tagged `scriptDialect: 'bru'`.
 */
export function parseBrunoRequest(text) {
  const node = bruRequestToNode(text);
  if (node.reqType === 'unsupported') {
    return { name: node.name, description: '', method: 'GET', url: '', headers: [], params: [], cookies: [], body: '', body_type: 'json', auth: defaultRequestAuth(), sort_order: node.seq || 0 };
  }
  return irRequestToNormalized(node, { sortOrder: node.seq || 0, warnings: [] });
}

function singleRequestCollection(node, collectionName, warnings) {
  const ir = { name: collectionName || node.name || 'Bruno Import', docs: '', defaults: emptyDefaults(), children: [{ ...node, seq: 0 }], environments: [] };
  return [irToNormalized(ir, warnings)];
}

/**
 * Parse a single .bru file into a NormalizedCollection with one request.
 * @returns {import('./types').NormalizedCollection[]}
 */
export function parseBruno(text, collectionName, warnings = []) {
  let node;
  try {
    node = bruRequestToNode(text);
  } catch (err) {
    warnings.push(`Failed to parse Bruno file: ${err.message}`);
    return [];
  }
  return singleRequestCollection(node, collectionName, warnings);
}

/** A single Bruno YAML request item (one `.yml` request file). */
export function parseBrunoYmlRequest(data, collectionName, warnings = []) {
  return singleRequestCollection(ymlItemToNode(data), collectionName, warnings);
}
